import { PlayerIdentity } from "components/ReviewAudit/PublicRatings"
import React from "react"
import { evaluateScore, matchFormatOf, scoreProblem } from "../../../../../../tenis10MatchFormat"

// The server remains responsible for the sporting validity of scores and winner.
// The first two sets retain the legacy 0–7 input range; set 3 is a match tie-break.
// A recorded set whose fields are all cleared is sent with null values: the server
// removes it, so a match that no longer needs its deciding set goes back to "—".
// Only the last recorded set can be removed this way, and never set 1: a blank first
// set or a gap in the sequence is still an input error.
export const clearedSet = score =>
  score.points === null && (score.tie === null || score.tie === undefined)
// RELEASE-TENIS10 § 11 P54 B1: a Tenis10 match takes only a score its format finishes (10–8, 12–10, 3–1), as the server does; an outcome
// (Didn't show up / Abandoned) is the way out. Its sets have no 0–7 cap.
const sideScore = (values, match, role) => values[role]?.score ?? match?.[role]?.score ?? []
const sideOutcome = (values, match, role) => values[role]?.reason ?? match?.[role]?.reason ?? ""
export const validateScoreValues = (values, match) => {
  const format = matchFormatOf(match)
  for (const role of ["player", "opponent"]) {
    const sets = values[role]?.score || []
    for (const score of sets) {
      if (clearedSet(score)) {
        const last = Math.max(...sets.filter(item => !clearedSet(item)).map(item => Number(item.set)), 0)
        if (Number(score.set) > 1 && Number(score.set) > last) continue
        return { score: "Enter whole numbers: 0–7 for set games and 0 or more for tie-breaks. Leave unplayed sets untouched." }
      }
      for (const field of ["points", "tie"]) {
        const value = score[field]
        if (field === "tie" && (value == null || value === "")) continue
        if (
          value === "" ||
          value == null ||
          !Number.isInteger(Number(value)) ||
          Number(value) < 0 ||
          (!format && field === "points" && score.set < 3 && Number(value) > 7)
        ) {
          return {
            score:
              "Enter whole numbers: 0–7 for set games and 0 or more for tie-breaks. Leave unplayed sets untouched.",
          }
        }
      }
    }
  }
  if (format && !["player", "opponent"].some(role => sideOutcome(values, match, role))) {
    const problem = scoreProblem(format, sideScore(values, match, "player"), sideScore(values, match, "opponent"))
    if (problem) return { score: problem }
  }
  return {}
}
const roles = ["player", "opponent"]
// The column of a Tenis10 set: a tiebreak (one, or 1–3), the short games, or a normal set.
const tenis10Column = (format, set) => format.kind === "tiebreak" ? (format.noOfSets === 1 ? "Tiebreak" : `Tiebreak ${set}`)
  : format.kind === "games" ? "Games" : "Set"
/** „Winner: Ana Testescu, 10–8.”: who the score names, or who wins by an outcome; nothing before. */
const tenis10Winner = (format, match, values) => {
  const outcome = roles.find(role => sideOutcome(values, match, role))
  if (outcome) {
    const winner = match[outcome === "player" ? "opponent" : "player"]
    return `Winner: ${playerName(winner, "")} — ${playerName(match[outcome], "")} ${sideOutcome(values, match, outcome) === "NO_SHOW" ? "did not show up" : "abandoned; the score stays where the match stopped"}.`
  }
  const reading = evaluateScore(format, sideScore(values, match, "player"), sideScore(values, match, "opponent"))
  if (reading.status !== "complete") return null
  const won = reading.winner
  return `Winner: ${playerName(match[won], won)}, ${reading.sets.map(set => won === "player" ? `${set.player}–${set.opponent}` : `${set.opponent}–${set.player}`).join(", ")}.`
}
export const scoreDraftChanged = (match, values) => {
  const normalize = scores => (scores || []).filter(set => [set.points, set.tie].some(value => value != null && value !== ""))
    .map(set => [Number(set.set), String(set.points ?? ""), String(set.tie ?? "")]).sort((left, right) => left[0] - right[0])
  return roles.some(role => String(values[role]?.reason ?? match?.[role]?.reason ?? "") !== String(match?.[role]?.reason ?? "")
    || JSON.stringify(normalize(values[role]?.score ?? match?.[role]?.score)) !== JSON.stringify(normalize(match?.[role]?.score)))
}
const playerName = (person, fallback) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ") || fallback

const ScoreForm = ({ match = {}, scoreValues, setFieldValue }) => {
  const format = matchFormatOf(match)
  const sets = Array.from(
    { length: format?.noOfSets || match.noOfSets || 3 },
    (_, index) => index + 1
  )
  const winnerLine = format && tenis10Winner(format, match, scoreValues)
  const updateScore = (role, set, field, value) => {
    const score = match[role]?.score || []
    const current = scoreValues[role] || {}
    const currentScore = current.score || score
    const existing = currentScore.find(item => Number(item.set) === set) || {
      set,
    }
    const updated = { ...existing, [field]: value === "" ? "" : Number(value) }
    const wasRecorded = score.some(item => Number(item.set) === set)
    const isEmpty = [updated.points, updated.tie].every(
      item => item == null || item === ""
    )
    const next = isEmpty ? { set, points: null, tie: null } : updated
    setFieldValue(role, {
      ...current,
      score: [
        ...currentScore.filter(item => Number(item.set) !== set),
        ...(isEmpty && !wasRecorded ? [] : [next]),
      ].sort((a, b) => a.set - b.set),
    })
  }
  const scoreInput = (role, set, field) => {
    const name = playerName(match[role], role)
    const currentScore = scoreValues[role]?.score || match[role]?.score || []
    const setScore = currentScore.find(item => Number(item.set) === set)
    const label =
      field === "tie"
        ? `Set ${set} tie-break`
        : format
        ? tenis10Column(format, set)
        : set === 3
        ? "Match tie-break"
        : `Set ${set} games`
    return (
      <input
        type="number"
        inputMode="numeric"
        min="0"
        step="1"
        max={!format && field === "points" && set < 3 ? 7 : undefined}
        className="form-control"
        aria-label={`${name} · ${label}`}
        value={setScore?.[field] ?? ""}
        placeholder="—"
        onWheel={event => event.currentTarget.blur()}
        onChange={event => updateScore(role, set, field, event.target.value)}
      />
    )
  }
  return (
    <div>
      <table className="main-draw-score-form table table-bordered">
        <caption className="sr-only">Match score entry</caption>
        <thead>
          <tr>
            <th scope="col">Player / team</th>
            {sets.map(set => (
              <th scope="col" key={set}>
                {format ? tenis10Column(format, set) : set === 3 ? "Match tie-break" : `Set ${set}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roles.map(role => (
            <tr key={role}>
              <th scope="row">
                <div><PlayerIdentity player={match[role]} to={null}>{playerName(match[role], role)}</PlayerIdentity></div>
                {match[`${role}Partner`]?.userId && (
                  <div><PlayerIdentity player={match[`${role}Partner`]} to={null}>{playerName(match[`${role}Partner`], "Partner")}</PlayerIdentity></div>
                )}
                <label className="main-draw-outcome">
                  Outcome
                  <select
                    className="form-control form-control-sm"
                    aria-label={`${playerName(match[role], role)} · Outcome`}
                    value={
                      scoreValues[role]?.reason ?? match[role]?.reason ?? ""
                    }
                    onChange={event =>
                      setFieldValue(role, {
                        ...scoreValues[role],
                        reason: event.target.value,
                        type: event.target.value ? "BYE" : "NONE",
                      })
                    }
                  >
                    <option value="">None</option>
                    <option value="NO_SHOW">Didn't show up</option>
                    <option value="ABANDONED">Abandoned</option>
                  </select>
                </label>
              </th>
              {sets.map(set => (
                <td key={set}>
                  {scoreInput(role, set, "points")}
                  {(format ? format.kind === "set" : set < 3) && (
                    <label className="main-draw-tie-inputs">
                      <span>Tie-break</span>
                      {scoreInput(role, set, "tie")}
                    </label>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {winnerLine && <p role="status" className="text-success small mb-0">{winnerLine}</p>}
    </div>
  )
}
export default ScoreForm
